import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import { InputGroup, InputGroupText, Input, ListGroup, ListGroupItem, Badge, Spinner, Button, Row, Col } from "reactstrap";
import { loadCoachesPage, loadStudentsPage, usersReset } from "../../redux/Messages/UsersPrepTalkAction";
import { ensureDMThread, createGroupThread } from "../../redux/Messages/ThreadsAction";
import { normEmail } from "../../util/email";
function PeopleList({
  meEmail, presenceItems,
  coaches, students, loadingCoaches, loadingStudents, hasMoreCoaches, hasMoreStudents, usersError,
  loadCoaches, loadStudents, resetUsers,
  onOpenDM, onOpenGroup, dispatch
}) {

  const presenceMap = useMemo(
    () => Object.fromEntries((presenceItems || []).map(p => [p.email, p])),
    [presenceItems]
  );

  const [q, setQ] = useState("");
  const [role, setRole] = useState("coaches");
  const [groupMode, setGroupMode] = useState(false);
  const [groupName, setGroupName] = useState("");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => { loadCoaches({ reset:true }); loadStudents({ reset:true }); return () => resetUsers(); }, [loadCoaches, loadStudents, resetUsers]);

  const containerRef = useRef(null), coachSentinel = useRef(null), studentSentinel = useRef(null);

  useEffect(() => {
    const rootEl = containerRef.current; if (!rootEl) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (!e.isIntersecting) return;
        if (e.target.id === "coachSentinel" && hasMoreCoaches && !loadingCoaches) loadCoaches();
        if (e.target.id === "studentSentinel" && hasMoreStudents && !loadingStudents) loadStudents();
      });
    }, { root: rootEl, rootMargin:"160px", threshold:0 });
    coachSentinel.current && obs.observe(coachSentinel.current);
    studentSentinel.current && obs.observe(studentSentinel.current);
    return () => obs.disconnect();
  }, [hasMoreCoaches, hasMoreStudents, loadingCoaches, loadingStudents, loadCoaches, loadStudents]);

  const filterMerge = (arr=[]) => {
    const qq = q.toLowerCase();
    return arr
      .filter(u => u.email && u.email !== normEmail(meEmail))
      .map(u => ({ ...u, online: !!presenceMap[u.email]?.online }))
      .filter(u => !qq || (u.displayName||"").toLowerCase().includes(qq) || u.email.toLowerCase().includes(qq))
      .sort((a,b) => Number(!!b.online)-Number(!!a.online) || (a.displayName||a.email).localeCompare(b.displayName||b.email));
  };

  const listCoaches=useMemo(()=>filterMerge(coaches),[coaches,meEmail,presenceMap,q]);
  const listStudents=useMemo(()=>filterMerge(students),[students,meEmail,presenceMap,q]);
  const rows = useMemo(()=> role==="coaches"?listCoaches : role==="students"?listStudents : filterMerge([...(coaches||[]),...(students||[])].filter(data => ["true", true].includes(data.admin))), [role,listCoaches,listStudents,coaches,students,meEmail,presenceMap,q]);

  const toggleSelect = (email) => {
    const s = new Set(selected);
    s.has(email) ? s.delete(email) : s.add(email);
    setSelected(s);
  };

  const startDM = async (email) => {
    const id = await ensureDMThread({ aEmail: meEmail, bEmail: email })(dispatch, ()=>({}));
    onOpenDM?.({ id, isGroup:false, name:null, participantsEmails:[normEmail(meEmail), normEmail(email)] });
  };

  const createGroup = async () => {
    const parts = Array.from(selected);
    const me = normEmail(meEmail);
    if (!parts.includes(me)) parts.push(me);
    const id = await createGroupThread({ name: groupName || "Group", participantsEmails: parts })(dispatch, ()=>({}));
    onOpenGroup?.({ id, isGroup:true, name: groupName || "Group", participantsEmails: parts.map(normEmail) });
    setGroupMode(false); setGroupName(""); setSelected(new Set());
  };   


  const colors = [
    "bg-gradient-blue",
    "bg-gradient-indigo",
    "bg-gradient-purple",
    "bg-gradient-pink",
    "bg-gradient-red",
    "bg-gradient-teal",
    "bg-gradient-cyan",
    "bg-gradient-primary",
    "bg-gradient-info",
    "bg-gradient-danger",
  ];

  function shuffle(array) {
    let arr = [...array];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }


  const shuffledColors = useMemo(() => shuffle(colors), []);
  

  return (
    <div className="d-flex flex-column h-100">
      <div className="p-2">
        <InputGroup className="mb-2"><InputGroupText><i class="fas fa-search text-primary font-weight-bolder text-lg"></i></InputGroupText><Input className="pl-2" placeholder="Search people…" value={q} onChange={e=>setQ(e.target.value)} /></InputGroup>
        
        <Row className="mx-0 w-100 my-2">
          
          <Col className="p-1"> 
            <button className={`btn btn-sm w-100 ${role==="coaches"?"btn-primary":"btn-outline-primary"}`} onClick={()=>setRole("coaches")}>Coaches</button>
          </Col>
          <Col className="p-1">
            <button className={`btn btn-sm w-100 ${role==="students"?"btn-primary":"btn-outline-primary"}`} onClick={()=>setRole("students")}>Students</button>
          </Col>
          <Col className="p-1">
            <button className={`btn btn-sm w-100 ${role==="all"?"btn-primary":"btn-outline-primary"}`} onClick={()=>setRole("all")}>Admin</button>
          </Col>
        </Row>
        <div className="d-flex justify-content-between align-items-center">
          <div className="small text-muted">{groupMode ? "Select at least 2 others to create a group" : "Tap a person to start a chat"}</div>
          <Button size="sm" color={groupMode?"secondary":"info"} onClick={()=>{ setGroupMode(!groupMode); setSelected(new Set()); }}>{groupMode?"Cancel":<><i class="fas fa-plus"></i> Group</>}</Button>
        </div>
        {groupMode && (
          <div className="mt-2 d-flex gap-2">
            <Input placeholder="Group name" value={groupName} onChange={e=>setGroupName(e.target.value)} />
            <Button size="sm" color="info" className="ml-1" disabled={selected.size<2} onClick={createGroup}><i class="fas fa-plus text-lg mx-2"></i></Button>
          </div>
        )}
        {usersError && <div className="text-danger small mt-2">{usersError}</div>}
      </div>

      <div ref={containerRef} className="px-2 pb-2 overflow-auto" style={{height:'100%'}}>
        <ListGroup flush>
          {rows.map((u, i) => (
            <ListGroupItem key={u.email} action className="d-flex align-items-center" 
              onClick={()=> groupMode ? toggleSelect(u.email) : startDM(u.email)}
              style={{ cursor:"pointer"}}>

              {groupMode &&<div className="ml-2 d-flex align-items-center"><Input type="checkbox" className="" onChange={()=>toggleSelect(u.email)} checked={selected.has(u.email)} onClick={(e)=>e.stopPropagation()} /></div> }

              <div className="position-relative">

                { !u.profilePicture ? 
                <div className={"msgr-avatar "+shuffledColors[i % shuffledColors.length]+" text-white"}><span>{(u.email||"?").slice(0,1).toUpperCase()}</span>
                
                </div>
                :
              <img 
                    src={"https://storage.googleapis.com/pre-dental-tracker.appspot.com/profile-picture/" + u.profilePicture}
                    className="msgr-avatar border border-dark" 
                    alt="..." 
                    style={{
                        objectFit:"cover"
                    }}
                /> }


                <span className={`position-absolute bottom-0 end-0 fas fa-circle msgr-dot  ${u.online ? 'text-success':'text-gray'}`} />

              </div>

              <div className="min-w-0 ml-1" style={{width:"85%"}}>
                
                <div className="min-w-0" >
                  
                  <div className=" text-truncate text-sm font-weight-bolder pr-2">{(u.displayName || "Student")}</div>
                    {/* {!r.isGroup && r.online && <Badge color="success" pill>Online</Badge>} */}
                    {/* {r.isGroup && <Badge color="info" pill>Group</Badge>} */}
                  <div className="text-muted small text-truncate" >{u.email}</div>
                  
                </div>
                <div className="d-flex align-items-center" >
                    {u.coach ? <Badge color="info" className="ms-2 text-capitalize font-weight-bolder">Coach</Badge> : <Badge color="warning" className="ms-2 text-capitalize font-weight-bolder">Student</Badge>}&nbsp;
                    {u.admin ? <Badge color="primary" className="ms-2 text-capitalize font-weight-bolder">Admin</Badge> : null}


                </div>
                
              </div>


              {/* <div className="flex-grow-1">
                <div className="fw-semibold">{u.displayName || u.email}</div>
                <div className="small text-muted">{u.email}</div>
              </div>
              <Badge color={u.online ? "success" : "secondary"} pill>{u.online ? "Online" : "Offline"}</Badge>
              {u.coach ? <Badge color="info" className="ms-2">Coach</Badge> : <Badge color="light" className="ms-2 text-muted">Student</Badge>} */}
              
            </ListGroupItem>
            
          ))}
          <div id="coachSentinel" ref={coachSentinel} />
          <div id="studentSentinel" ref={studentSentinel} />
          {(loadingCoaches || loadingStudents) && <ListGroupItem className="text-center"><Spinner size="sm" /> Loading…</ListGroupItem>}
          {!loadingCoaches && !loadingStudents && rows.length === 0 && <ListGroupItem className="text-muted">No people found</ListGroupItem>}
        </ListGroup>
      </div>
    </div>
  );
}

export default connect(
  state => ({
    coaches: state.usersPreptalk.coaches, 
    students: state.usersPreptalk.students,
    loadingCoaches: state.usersPreptalk.loadingCoaches, 
    loadingStudents: state.usersPreptalk.loadingStudents,
    hasMoreCoaches: state.usersPreptalk.hasMoreCoaches, 
    hasMoreStudents: state.usersPreptalk.hasMoreStudents,
    usersError: state.usersPreptalk.error,
    presenceItems: state.presence.items
  }),
  d => ({
    loadCoaches: (a)=>d(loadCoachesPage(a)),
    loadStudents: (a)=>d(loadStudentsPage(a)),
    resetUsers: ()=>d(usersReset()),
    dispatch: d
  })
)(PeopleList);
